// src/components/common/ConnectionLostOverlay.tsx
import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNetwork } from '../../context/NetworkContext';
import { FiWifiOff, FiRefreshCw } from 'react-icons/fi';
import { GlassCard, Button } from '../common';

const ConnectionLostOverlay: React.FC = () => {
  const { isOnline, refreshPage } = useNetwork();
  const [showOverlay, setShowOverlay] = useState(false);
  
  // Add a slight delay before showing the overlay to prevent flashing
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    
    if (!isOnline) {
      timeoutId = setTimeout(() => {
        setShowOverlay(true);
      }, 1500);
    } else {
      setShowOverlay(false);
    }
    
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [isOnline]);

  return (
    <AnimatePresence>
      {showOverlay && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm"
          />
          
          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="z-10 w-full max-w-md m-4"
          >
            <GlassCard variant="dark" elevated className="p-6">
              <div className="text-center">
                <div className="mb-6 p-4 mx-auto rounded-full bg-red-600/30 text-red-500 w-16 h-16 flex items-center justify-center">
                  <FiWifiOff size={32} />
                </div>
                
                <h3 className="text-xl font-semibold mb-2 text-white">Lost in Space</h3>
                
                <p className="text-white/70 mb-6">
                  It seems your internet connection is offline. Please reconnect to continue your SpeedMeet session.
                </p>
                
                <div className="flex justify-center">
                  <Button
                    variant="primary"
                    onClick={refreshPage}
                    leftIcon={<FiRefreshCw size={18} />}
                    glowEffect
                  >
                    Refresh Page
                  </Button>
                </div>

                <p className="text-xs text-white/50 mt-4">
                  The page will automatically refresh once your connection is restored.
                </p>
              </div>
            </GlassCard>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default ConnectionLostOverlay;
